import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  structuredData?: object;
}

// Analytics tracking helper (privacy-compliant, no personal data)
const trackPageView = (page: string, title: string) => {
  // Only track if analytics is enabled (can be set via environment variable)
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('config', 'GA_MEASUREMENT_ID', {
      page_path: page,
      page_title: title,
    });
  }
  
  // Console log for development (can be replaced with actual analytics)
  if (import.meta.env.DEV) {
    console.log('[Analytics] Page view:', { page, title });
  }
};

const SEOHead: React.FC<SEOProps> = ({
  title = 'LRDEnE - AI-Powered Legal Assistant for Germany & EU',
  description = 'LRDEnE provides AI-powered legal document analysis, template generation, and legal advice for German and EU law. Streamline your legal workflow with intelligent automation.',
  keywords = 'legal AI, German law, EU law, legal document analysis, GDPR, legal templates, legal assistant, contract analysis',
  image = '/og-image.png', // Default OG image
  url,
  type = 'website',
  noindex = false,
  structuredData,
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Track page view for analytics
    trackPageView(window.location.pathname, title);

    // Get or create meta tags
    const updateOrCreateMeta = (name: string, content: string, attribute: string = 'name') => {
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateOrCreateMeta('description', description);
    updateOrCreateMeta('keywords', keywords);
    
    if (noindex) {
      updateOrCreateMeta('robots', 'noindex, nofollow');
    } else {
      updateOrCreateMeta('robots', 'index, follow');
    }

    // Open Graph tags
    const ogImage = image.startsWith('http') ? image : `${window.location.origin}${image}`;
    updateOrCreateMeta('og:title', title, 'property');
    updateOrCreateMeta('og:description', description, 'property');
    updateOrCreateMeta('og:type', type, 'property');
    updateOrCreateMeta('og:image', ogImage, 'property');
    updateOrCreateMeta('og:url', url || window.location.href, 'property');
    updateOrCreateMeta('og:site_name', 'LRDEnE', 'property');
    updateOrCreateMeta('og:locale', 'en_US', 'property');

    // Twitter Card tags
    updateOrCreateMeta('twitter:card', 'summary_large_image');
    updateOrCreateMeta('twitter:title', title);
    updateOrCreateMeta('twitter:description', description);
    updateOrCreateMeta('twitter:image', ogImage);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url || window.location.href);

    // Structured data (JSON-LD)
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (structuredData) {
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(structuredDataScript);
      }
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else if (structuredDataScript) {
      // Remove if no structured data
      structuredDataScript.remove();
    }
  }, [title, description, keywords, image, url, type, noindex, structuredData]);

  return null; // This component doesn't render anything
};

export default SEOHead;

